/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from "react";
import {
  IconDownload,
  IconPlus,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react";
import CreateZoneAreaModal from "./CreateZoneAreaModal";
import CreateRowModal from "./CreateRowModal";
import { useForm } from "react-hook-form";
import RoleToggle from "../../../components/RoleToggle";
import { useWarehouseListData } from "../../../apis/warehouseApi";
import {
  useDownloadShelveBarcode,
  useFetchShelfAllData,
  useFetchShelfRowsAllData,
  useShelfCreateMutation,
  useShelfRowCreateMutation,
  useShelfUpdateMutation,
} from "../../../hooks/shelveHooks";
import toast from "react-hot-toast";
import Row from "./RowElement";
import { Shelf } from "../../../apis/shelveApis";
import { useProfileData } from "../../../apis/profileApis";
import EditRowModal from "./EditRowModal";
import { useShelfRowUpdateMutation } from "../../../hooks/shelveHooks";

export default function ShelveLocationTab() {
  const [modalType, setModalType] = useState<"edit" | "create" | undefined>();
  const [modal, setModal] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [selectedZone, setSelectedZone] = useState<any>(null);
  const [row, setRow] = useState(false);
  const [open, setOpen] = useState<boolean | string>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [editRowOpen, setEditRowOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<any>();

  const {
    register: rowRegister,
    handleSubmit: handleRowSubmit,
    clearErrors: rowClearErrors,
    reset: rowReset,
    setValue: setRowValue,
    watch: rowWatch,
    control: rowControl,
    formState: { errors: rowErrors },
  } = useForm<any>();

  const { data: warehouses, refetch } = useWarehouseListData();
  const { data: UserData } = useProfileData();
  const { mutate, isPending } = useShelfCreateMutation();
  const { mutate: updateShelf } = useShelfUpdateMutation(selectedZone?.id);
  const { mutate: updateRow, isPending: isUpdatingRow } =
    useShelfRowUpdateMutation(selectedRow?.id);

  const [warehouse, setWarehouse] = useState<string>(
    () =>
      localStorage.getItem("WarehouseActiveRole") ||
      warehouses?.warehouses?.[0]?.name,
  );
  useEffect(() => {
    localStorage.setItem("WarehouseActiveRole", warehouse);
  }, [warehouse]);

  const { mutate: rowMutate, isPending: isRowPending } =
    useShelfRowCreateMutation();
  const { data: shelves, refetch: fetchShelves } = useFetchShelfAllData();

  const [area, setArea] = useState<string>(
    () => localStorage.getItem("AreaActiveRole") || (shelves?.[0]?.name ?? ""),
  );
  useEffect(() => {
    localStorage.setItem("AreaActiveRole", area);
  }, [area]);

  const { data: shelveRows, refetch: refetchShelveRows } =
    useFetchShelfRowsAllData(
      shelves?.find((shelve: any) => shelve?.name === area)?.id ?? "",
      warehouses?.warehouses?.find(
        (warehouseData: any) => warehouseData?.name === warehouse,
      )?.id,
    );

  const { mutateAsync: fetchBarcode, isPending: isDownloading } =
    useDownloadShelveBarcode();

  const handleCreateShelve = (formData: any) => {
    const payload = {
      name: formData.name,
      ...formData,
    };
    mutate(payload, {
      onSuccess: () => {
        toast.success("Area created successfully");
        setModal(false);
        refetch();
      },
      onError: (error) => toast.error(`${error}`),
    });
  };

  const handleUpdateShelve = (formData: any) => {
    const payload = {
      name: formData.name,
    };

    // 👉 You NEED update mutation (you don’t have it yet)
    updateShelf(payload, {
      onSuccess: () => {
        toast.success("Area updated successfully");
        setModal(false);
        setSelectedZone(null);
        refetch();
      },
      onError: (err: any) => {
        toast.error(err?.message || "Update failed");
      },
    });
  };

  const handleCreateShelveRow = (formData: any) => {
    const payload = {
      outletShelveId: shelves?.find((shelve: any) => shelve?.name === area)?.id,
      ...formData,
    };
    rowMutate(payload, {
      onSuccess: () => {
        toast.success("Rack with levels created successfully");
        setRow(false);
        refetch();
        refetchShelveRows();
      },
      onError: (error) => toast.error(`${error}`),
    });
  };

  const handleUpdateShelveRow = (formData: any) => {
    const payload = {
      outletShelveId: selectedRow?.outletShelveId,
      outletRackId: selectedRow?.id,
      name: formData.rackName,
      levels: formData.levels.map((lvl: any, index: number) => {
      const base = {
        name: lvl.name,
        levelNumber: index + 1,
      };

      if (lvl.id) {
        return {
          ...base,
          id: lvl.id,
        };
      }

      return base;
    }),
  };

    updateRow(payload, {
      onSuccess: () => {
        setEditRowOpen(false);
        setSelectedRow(null);
        toast.success("Rack and levels updated successfully");
        refetchShelveRows();
      },
      onError: (err: any) => {
        toast.error(err?.message || "Update failed");
      },
    });
  };

  const handleDownload = async () => {
    const outletShelveId = shelves?.find((s: any) => s?.name === area)?.id;

    if (!outletShelveId) {
      toast.error("Please select a valid Area");
      return;
    }

    const res = await fetchBarcode({ outletShelveId });
    if (res?.pdfUrl) {
      window.open(res.pdfUrl, "_blank");
    } else {
      toast.error("Failed to download barcode PDF");
    }
  };

  return (
    <div className="pb-4">
      <div className="flex  flex-row items-center gap-4">
        <div className="flex-1 min-w-0">
          {" "}
          <div className="bg-[#F7F6F2] flex items-center justify-between px-4 py-2 w-full rounded-tl-3xl rounded-tr-3xl lg:rounded-tr-[40px] overflow-hidden">
            <div className="flex items-center space-x-4 overflow-x-auto scrollbar-hide whitespace-nowrap py-1">
              <button
                className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-full
              transition-colors duration-200 bg-[#303030] text-white"
              >
                {UserData?.outletName ?? UserData?.name}
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* EDIT */}
          <button
            onClick={() => {
              const selected = shelves?.find((s: any) => s.name === area);
              setMode("edit");
              setSelectedZone(selected); // ✅ correct data
              setModal(true);
            }}
            className="flex items-center justify-center w-12 h-12 rounded-full border border-[#FFFFFF] bg-[#F7F6F2] shadow-sm hover:shadow"
          >
            <IconPencil stroke={1.5} size={24} />
          </button>

          {/* CREATE */}
          <button
            onClick={() => {
              setMode("create");
              setSelectedZone(null);
              setModal(true);
            }}
            className="flex items-center justify-center w-12 h-12 rounded-full border border-[#FFFFFF] bg-[#F7F6F2] shadow-sm hover:shadow"
          >
            <IconPlus stroke={1.5} size={24} />
          </button>
        </div>
      </div>

      <div className="bg-[#F7F6F2] p-2 rounded-r-3xl rounded-b-3xl  lg:mt-0">
        <div className="bg-[#FCFDF8] rounded-3xl border overflow-hidden">
          {shelves && shelves?.length > 0 ? (
            <div className="flex  flex-row items-center justify-between gap-4 border-b p-4">
              <div className="overflow-x-auto scrollbar-hide whitespace-nowrap ">
                <RoleToggle
                  options={shelves.map((shelve: any) => shelve?.name)}
                  active={area}
                  onChange={setArea}
                />
              </div>

              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={handleDownload}
                  className="p-2.5 rounded-full border border-[#F7F6F2] "
                >
                  <IconDownload stroke={1.5} size={20} />
                </button>
                {/* <button className="p-2.5 rounded-full bg-[#F7F6F2] border border-[#FFFFFF]">
                  <IconTrash stroke={1.5} size={20} />
                </button>
                <button className="p-2.5 rounded-full bg-[#F7F6F2] border border-[#FFFFFF]">
                  <IconPencil stroke={1.5} size={20} />
                </button> */}
                <button
                  onClick={() => {
                    setRowValue(
                      "storageType",
                      shelves?.find((shelve: Shelf) => shelve?.name === area)
                        ?.storageType === "B2C"
                        ? "Shelves"
                        : "Bins",
                    );
                    setRow(true);
                    setModalType("create");
                  }}
                  className="p-2.5 rounded-full bg-[#F7F6F2] border border-[#FFFFFF]"
                >
                  <IconPlus stroke={1.5} size={20} />
                </button>
              </div>
            </div>
          ) : null}

          {/* Empty States – 100% same as yours */}
          {!shelves || shelves.length === 0 ? (
            <div className="p-8 flex flex-col items-center space-y-6">
              <img
                src="/img/scan-data.png"
                alt="scan prompt"
                className="w-48"
              />
              <div className="text-center max-w-sm">
                <h2 className="text-[#212121] text-lg font-medium">
                  Create Zone & Area
                </h2>
                <p className="text-[#212121]/70 text-sm mt-1">
                  You don't have any Area yet. Create one using the button
                  above.
                </p>
              </div>
              <button
                onClick={() => setModal(true)}
                className="flex items-center gap-2 py-2.5 px-6 text-sm rounded-full text-[#212121] bg-[#FBD037] font-medium hover:bg-yellow-400 transition"
              >
                <IconPlus stroke={1.5} size={20} /> Create Area
              </button>
            </div>
          ) : shelveRows?.length === 0 ? (
            <div className="p-8 flex flex-col items-center space-y-6">
              <img
                src="/img/scan-data.png"
                alt="scan prompt"
                className="w-48"
              />
              <div className="text-center max-w-sm">
                <h2 className="text-[#212121] text-lg font-medium">Add Rows</h2>
                <p className="text-[#212121]/70 text-sm mt-1">
                  You don't have any Row yet. Create one using the button above.
                </p>
              </div>
              <button
                onClick={() => {
                  setRowValue(
                    "storageType",
                    shelves?.find((shelve: Shelf) => shelve?.name === area)
                      ?.storageType === "B2C"
                      ? "Shelves"
                      : "Bins",
                  );
                  setRow(true);
                  setModalType("create");
                }}
                className="flex items-center gap-2 py-2.5 px-6 text-sm rounded-full text-[#212121] bg-[#FBD037] font-medium hover:bg-yellow-400 transition"
              >
                <IconPlus stroke={1.5} size={20} /> Create Row
              </button>
            </div>
          ) : (
            /* Your grid – now responsive but visually identical */
            <div className="grid grid-cols-5 gap-10 [@media(min-width:1920px)]:gap-16 [@media(min-width:1536px)]:gap-10 bg-[repeating-linear-gradient(45deg,#eaeaea_0px,#eaeaea_2px,#F2F2F2_2px,#F2F2F2_10px)] overflow-x-auto overflow-hidden">
              {shelveRows?.map((rack: any, index: number) => (
                <Row
                  key={`${rack?.id}`}
                  rowType={rack?.storageType}
                  rowId={rack?.id}
                  name={rack.name}
                  idx={index + 1}
                  className="w-full"
                  rackLevels={rack.levels}
                  racks={rack}
                  outletShelveId={rack.outletShelveId}
                  clientId={rack.clientId}
                  storageType={rack.storageType}
                  fetchShelves={refetchShelveRows}
                  onEdit={() => {
                    setSelectedRow(rack);
                    setEditRowOpen(true);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {modal && (
        <CreateZoneAreaModal
          open={modal}
          mode={mode}
          zoneItem={selectedZone}
          onClose={() => {
            setModal(false);
            reset();
            setSelectedZone(null);
          }}
          register={register}
          errors={errors}
          clearErrors={clearErrors}
          setValue={setValue}
          watch={watch}
          isPending={isPending}
          handleSubmit={handleSubmit(
            mode === "edit" ? handleUpdateShelve : handleCreateShelve,
          )}
        />
      )}
      {row && (
        <CreateRowModal
          open={row}
          modalType={modalType}
          onClose={() => {
            setRow(false);
            rowReset();
          }}
          setValue={setRowValue}
          watch={rowWatch}
          register={rowRegister}
          errors={rowErrors}
          clearErrors={rowClearErrors}
          isPending={isRowPending}
          control={rowControl}
          handleSubmit={handleRowSubmit(handleCreateShelveRow)}
        />
      )}
      <EditRowModal
        open={editRowOpen}
        onClose={() => {
          setEditRowOpen(false);
          setSelectedRow(null);
          rowReset();
        }}
        register={rowRegister}
        control={rowControl}
        setValue={setRowValue}
        errors={rowErrors}
        isPending={isUpdatingRow}
        handleSubmit={handleRowSubmit(handleUpdateShelveRow)}
        defaultValues={selectedRow}
      />
    </div>
  );
}
